import type { ReactNode } from "react";
import { MideyeTopBarShell } from "@mideye/top-bar";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { INSIGHT_APP_NAME, InsightAppIcon } from "@/appIdentity.tsx";
import { useFeatureFlags } from "@/hooks/useFeatureFlags.tsx";
import { useVulnerabilityWatchBadgeCount } from "@/hooks/useVulnerabilityWatchSeen.ts";
import { buildNavItems } from "./navItems.tsx";
import UserMenu from "./UserMenu.tsx";
import IpSearchBox from "./IpSearchBox.tsx";

const launcherEnvironment = detectEnvironment();
const showCrossAppNavigation = readCrossAppNavigationFeatureFlag();

// The same production build runs on both the dev and prod clusters, so
// import.meta.env.PROD can't tell them apart — detect from the runtime host so
// cross-app links resolve to the matching environment.
function detectEnvironment(): "dev" | "prod" {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  if (host === "localhost" || host === "127.0.0.1" || host.includes(".dev.")) return "dev";
  return "prod";
}

function readCrossAppNavigationFeatureFlag(): boolean {
  // On by default; set VITE_SHOW_CROSS_APP_NAVIGATION=false to disable.
  return import.meta.env.VITE_SHOW_CROSS_APP_NAVIGATION !== "false";
}

interface AppShellProps {
  children: ReactNode;
}

export default function AppShell({ children }: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, user, login, error } = useAuth();
  const flags = useFeatureFlags();
  const vulnWatchCount = useVulnerabilityWatchBadgeCount();

  return (
    <MideyeTopBarShell
      appId="dashboard"
      appName={INSIGHT_APP_NAME}
      appIcon={<InsightAppIcon />}
      environment={launcherEnvironment}
      theme="light"
      userEmail={user?.email}
      isAuthenticated={isAuthenticated}
      isLoading={isLoading}
      signedOut={{
        title: "Sign in required",
        description: "Sign in to view your security dashboard",
        appName: INSIGHT_APP_NAME,
        onSignIn: login,
        signInLabel: "Sign In",
        error,
      }}
      navItems={buildNavItems(flags, location.pathname, vulnWatchCount)}
      onNavigate={navigate}
      onBrandClick={() => navigate("/")}
      showCrossAppNavigation={showCrossAppNavigation}
      rightContent={
        isAuthenticated ? (
          <>
            <IpSearchBox />
            <UserMenu />
          </>
        ) : undefined
      }
    >
      {children}
    </MideyeTopBarShell>
  );
}
